import axios from 'axios';
import {
    clearDashboardAuthSession,
    getDashboardAccessToken,
    getDashboardEmail,
    getDashboardRefreshToken,
    saveDashboardAuthSession,
} from '../utils/dashboardAuth';

const apiClient = axios.create({
    baseURL: `${process.env.REACT_APP_BASE_URL}/`,
    withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
    const token = getDashboardAccessToken();
    if (token) {
        config.headers = {
            ...config.headers,
            Authorization: token,
        };
    }
    return config;
});

const renewAccessToken = async () => {
    const refreshToken = getDashboardRefreshToken();
    const email = getDashboardEmail();

    if (!refreshToken || !email) {
        throw new Error('Missing credentials to renew access token');
    }

    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/renew-access-token`, {
        refreshToken,
        email,
    }, {
        withCredentials: true,
    });

    saveDashboardAuthSession(response?.data, email);
    const newAccessToken = getDashboardAccessToken();

    if (!newAccessToken) {
        throw new Error('Missing access token in renew response');
    }

    apiClient.defaults.headers.common['Authorization'] = newAccessToken;

    return newAccessToken;
};

let refreshPromise = null;

const getAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = renewAccessToken()
            .catch((error) => {
                clearDashboardAuthSession();
                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const errorResponse = error?.response;

        if (!originalRequest || originalRequest._retry) {
            return Promise.reject(error);
        }

        if (!errorResponse) {
            return Promise.reject(error);
        }

        if (originalRequest?.url?.includes('/auth/renew-access-token')) {
            return Promise.reject(error);
        }

        const statusCode = errorResponse.status;
        const errorCode = errorResponse?.data?.code || errorResponse?.data?.message || errorResponse?.data?.error;
        const shouldAttemptRenew = (statusCode === 401 || statusCode === 403) && errorCode === 'INVALID_ACCESS_TOKEN';

        if (!shouldAttemptRenew) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
            const newToken = await getAccessToken();
            if (newToken) {
                originalRequest.headers = {
                    ...originalRequest.headers,
                    Authorization: newToken,
                };
            }
            return apiClient(originalRequest);
        } catch (err) {
            return Promise.reject(err);
        }
    }
);

export default apiClient;